import { Theme, createTheme, ThemeProvider, CssBaseline, useTheme } from "@mui/material";
import { orange } from "@mui/material/colors";
import { deepmerge } from "@mui/utils";
import { Theme as PortalFrameworkTheme } from "@sal/portal-theme-default";
import type {} from "@mui/x-date-pickers/themeAugmentation";

// Setup theme values independent of existing theme variables
const currentPortalDefaultTheme = {
	palette: {
		primary: {
			main: "#004985"
		},
		secondary: {
			main: orange[500]
		}
	},
	components: {
		MuiFormControl: {
			defaultProps: {
				variant: "outlined",
				size: "small"
			}
		},
		MuiTextField: {
			defaultProps: {
				variant: "outlined",
				size: "small"
			}
		},
		MuiPickersTextField: {
			defaultProps: {
				variant: "outlined",
				size: "small"
			}
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					"&.Mui-disabled": {
						backgroundColor: "rgba(0, 0, 0, 0.08)",
						borderRadius: "10px",
						cursor: "not-allowed"
					}
				},
				input: {
					"&.Mui-disabled": {
						WebkitTextFillColor: "rgba(0, 0, 0, 0.65)",
						cursor: "not-allowed"
					}
				}
			}
		},
		MuiPickersOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: "10px"
				},
				notchedOutline: {
					borderRadius: "10px"
				}
			}
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					borderRadius: "10px !important",
					//To disable arrows on number fields
					"& input[type=number]::-webkit-inner-spin-button": {
						WebkitAppearance: "none",
						margin: 0
					}
				}
			}
		},
		MuiTabScrollButton: {
			styleOverrides: {
				root: {
					//To stop tabs from moving right when tabs become scrollable
					"&.Mui-disabled": {
						display: "none"
					}
				}
			}
		},
		MuiSelect: {
			styleOverrides: {
				root: {
					borderRadius: "10px"
				}
			}
		},
		MuiTablePagination: {
			styleOverrides: {
				root: {
					//To hide the scrollbar appears beside pagination
					overflow: "hidden"
				}
			}
		},
		MuiTableContainer: {
			styleOverrides: {
				root: {
					marginBottom: "16px"
				}
			}
		},
		MuiTable: {
			defaultProps: {
				size: "small"
			}
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					border: "1px solid rgba(224, 224, 224, 1)",
					padding: "4px 8px"
				}
			}
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					"& .MuiTableCell-root": {
						backgroundColor: "#004985",
						color: "#fff",
						fontWeight: "bold"
					}
				}
			}
		},
		MuiTableBody: {
			styleOverrides: {
				root: {
					"& .MuiTableRow-root:nth-of-type(odd) .MuiTableCell-root": {
						backgroundColor: "#FFFFFF"
					},
					"& .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root": {
						backgroundColor: "#CCDAE6"
					}
				}
			}
		}
	},
	typography: {
		fontFamily: '"DaxlinePro", sans-serif',
		h1: {
			fontSize: "2.3rem",
			fontWeight: 500
		},
		h3: {
			fontSize: "1.5rem"
		},
		subtitle1: {
			fontSize: "1.1rem",
			fontWeight: 500
		}
	}
};

export const currentPortalTheme = (defaultTheme: Theme) => {
	let combinedTheme = createTheme(deepmerge(defaultTheme, currentPortalDefaultTheme));

	// Access existing theme palettes and other values here using combined Theme (e.g. combinedTeheme.palette.text.primary)
	return createTheme(combinedTheme, {
		components: {
			MuiAppBar: {
				styleOverrides: {
					root: {
						borderBottom: "1px solid " + combinedTheme.palette.divider,
						color: combinedTheme.palette.text.primary
					}
				}
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: combinedTheme.palette.primary.main
						}
					}
				}
			},
			MuiPickersOutlinedInput: {
				styleOverrides: {
					root: {
						"&:hover .MuiPickersOutlinedInput-notchedOutline": {
							borderColor: combinedTheme.palette.primary.main
						}
					}
				}
			},
			MuiCssBaseline: {
				styleOverrides: {
					"*": {
						scrollbarWidth: "thin",
						fontFamily: '"DaxlinePro", sans-serif'
					},
					".MuiPickersOutlinedInput-root": {
						height: "40px",
						borderRadius: "10px",
						fontSize: "0.875rem",
						display: "flex",
						alignItems: "center"
					},
					".MuiPickersOutlinedInput-notchedOutline": {
						borderRadius: "10px"
					},
					".MuiPickersOutlinedInput-sectionsContainer, .MuiPickersInputBase-sectionsContainer": {
						padding: "8.5px 0"
					},
					".MuiPickersTextField-root .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
						transform: "translate(14px, 9px) scale(1)"
					}
				}
			}
		}
	});
};

type PortalThemeProps = {
	children?: React.ReactNode;
}

function CustomThemeWrapper({ children }: PortalThemeProps) {
	const outerTheme = useTheme();
	const customTheme = currentPortalTheme(outerTheme);

	return (
		<ThemeProvider theme={customTheme}>
			<CssBaseline />
			{children}
		</ThemeProvider>
	);
}

export function PortalTheme({
	children
}: PortalThemeProps) {
	return (
		<PortalFrameworkTheme>
			<CustomThemeWrapper>
				{children}
			</CustomThemeWrapper>
		</PortalFrameworkTheme>
	);
}
